import { AnyAction, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ApiError } from '../../services/api';
import { RootState } from '../rootReducer';
import { IUser } from './user';
import {
  fetchUsersList
} from './list.thunks';
import { updateSectionAccessPermissions } from './updateSectionAccessPermissions.thunks';

export interface UserListState {
  users: IUser[];
  totalCount: number;
  unsorted: IUser[];
  loading: boolean;
  error: ApiError | null;
  editingUser: string;
  isDirty: boolean;
}

const initialState: UserListState = {
  error: null,
  loading: false,
  users: [],
  totalCount: 0,
  unsorted: [],
  editingUser: '',
  isDirty: false
};

const getUserListState = (state:RootState):UserListState => state.users.list;
const getError = (state:RootState):ApiError | null => getUserListState(state).error;
const getList = (state:RootState):IUser[] => getUserListState(state).users;
const getTotalCount = (state:RootState):number => getUserListState(state).totalCount;
const getUnsortedList = (state:RootState):IUser[] => getUserListState(state).unsorted;
const getLoading = (state:RootState):boolean => getUserListState(state).loading;
const getEditingUserId = (state:RootState):string => getUserListState(state).editingUser;
const getIsUserListDirty = (state: RootState): boolean => getUserListState(state).isDirty;

export const selectors = {
  getError,
  getList,
  getTotalCount,
  getUnsortedList,
  getLoading,
  getEditingUserId,
  getIsUserListDirty
};

export const extraReducers = {
  [fetchUsersList.pending.type]: (state: UserListState):void => {
    state.loading = true;
    state.users = [];
    state.totalCount = 0;
    state.unsorted = [];
  },
  [fetchUsersList.fulfilled.type]: (state: UserListState, action: AnyAction):void => {
    state.loading = false;
    state.totalCount = action.payload.count ?? (action.payload.accounts ? action.payload.accounts.length : 0 );
    state.users = action.payload.users;
    state.unsorted = [...(action.payload.users || [])];
    state.isDirty = false;
  },
  [fetchUsersList.rejected.type]: (state: UserListState, action: AnyAction):void => {
    state.loading = false;
    state.error = action.payload;
  },
  [updateSectionAccessPermissions.fulfilled.type]: (state: UserListState): void => {
    state.isDirty = true;
  }
};

export type UpdateListPayload = {
  newList: IUser[];
}

export type UpdateTableQueryPayload = string;

const usersListSlice = createSlice({
  name: 'usersList',
  initialState,
  reducers: {
    setEditingUser: (state:UserListState, action: PayloadAction<string>) => {
      state.editingUser = action.payload;
    },
    clearEditingUser: (state:UserListState) => {
      state.editingUser = '';
    },
    updateUser: (state:UserListState, action:PayloadAction<IUser>) => {
      const udpatedUser = action.payload;
      state.unsorted = state.unsorted.map(user => user.id === udpatedUser.id ? { ...user, ...udpatedUser } : user);
      state.users = [...state.unsorted];
    }
  },
  extraReducers
});

export default usersListSlice.reducer;
export const actions = {
  ...usersListSlice.actions,
  fetchUsersList
};
